import React from 'react';
import Stack from '../../v2BaseComponents/Stack';
import { View } from 'react-native';
import { Portal, PortalHost } from '@gorhom/portal';
import { colors } from '../../../utils/theme';
import { useAppType } from '../../../hooks/useAppType';
import DrawerHeaderImage from './DrawerHeaderImage';
import { useIsFocused } from '@react-navigation/native';

export const DrawerElementContext = React.createContext<React.ReactElement | null>(null);

export const DrawerElementProvider = ({
    children,
    drawerElement,
}: {
    children: React.ReactElement;
    drawerElement: React.ReactElement;
}) => {
    return <DrawerElementContext.Provider value={drawerElement}>{children}</DrawerElementContext.Provider>;
};

export const HasHeaderPortalProvider = React.createContext<boolean>(false);

const headerPortalName = 'header-portal';

export const HeaderPortalHostContainer: React.FC<{}> = () => (
    <HasHeaderPortalProvider.Provider value={true}>
        <PortalHost name={headerPortalName} />
    </HasHeaderPortalProvider.Provider>
);

export const useHasHeaderPortal = () => React.useContext(HasHeaderPortalProvider);

export const MaybeRenderHeaderInHeaderPortal: React.FC<{ children: any }> = ({ children }) => {
    const hasPortal = useHasHeaderPortal();
    const isFocused = useIsFocused();

    if (!isFocused) return null;

    return hasPortal ? (
        <Portal hostName={headerPortalName}>
            <HasHeaderPortalProvider.Provider value={false}>{children}</HasHeaderPortalProvider.Provider>
        </Portal>
    ) : (
        children
    );
};

export const MaybeDrawerLayout: React.FC<{ children: any }> = ({ children }) => {
    const isProApp = useAppType() === 'professional';
    const drawerElement = React.useContext(DrawerElementContext);

    if (!drawerElement) return children;

    return (
        <HasHeaderPortalProvider.Provider value={true}>
            <Stack vertical fullHeight fullWidth>
                <Stack
                    horizontal
                    fullWidth
                    style={{
                        height: 62,
                        backgroundColor: colors[isProApp ? 'Pro-Drawer-Background' : 'Drawer-Background'],
                    }}
                >
                    <DrawerHeaderImage />
                    <Stack horizontal fullHeight flex={1} align="flex-end">
                        <HeaderPortalHostContainer />
                    </Stack>
                </Stack>
                <Stack horizontal fullWidth flex={1}>
                    {drawerElement}
                    <View
                        style={{
                            flex: 1,
                            height: '100%',
                        }}
                        testID="drawer-main-content-content"
                    >
                        {children}
                    </View>
                </Stack>
            </Stack>
        </HasHeaderPortalProvider.Provider>
    );
};
